import { useState } from "react";
import { toast } from "sonner";
import { Section, Container, Eyebrow, DisplayH1 } from "@/components/Layout";
import { api } from "@/lib/api";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";

const TIERS = [
  {
    slug: "monthly",
    name: "Monthly",
    price: "$79",
    unit: "/ month",
    perks: [
      "Weekly livestream with faculty",
      "Full course library access",
      "Monthly webinars",
      "Private forums & Q&A",
      "AI Assistant (unlimited)",
      "10% off shop & books",
    ],
  },
  {
    slug: "annual",
    name: "Annual",
    price: "$790",
    unit: "/ year · save $158",
    perks: [
      "Everything in Monthly",
      "Annual Summit VIP ticket",
      "Exclusive apparel drop",
      "Early registration to bootcamps",
      "Signed founder's edition book",
      "Country ambassador invitation",
    ],
    highlight: true,
  },
];

export default function Membership() {
  const [form, setForm] = useState({ name: "", email: "", tier: "monthly" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/membership/join", form); toast.success("You're on the founding list."); setForm({ name: "", email: "", tier: form.tier }); }
    catch { toast.error("Could not sign up. Try again."); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Section testid="membership-hero" className="pt-40 pb-16">
        <Container>
          <Eyebrow>Wolfkeeper Plus</Eyebrow>
          <DisplayH1 className="mt-6 max-w-4xl">A private community of<br />serious dog people.</DisplayH1>
          <p className="mt-8 max-w-2xl text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
            Livestreams, exclusive documentaries, monthly challenges, and 24/7 AI coaching —
            everything you need between sessions with the University.
          </p>
        </Container>
      </Section>

      <Section testid="membership-tiers" className="pb-16">
        <Container>
          <div className="grid gap-8 md:grid-cols-2 max-w-4xl">
            {TIERS.map((t) => (
              <div key={t.slug} className={`card-lux p-10 ${t.highlight ? "border-[color:var(--wk-gold)]/60" : ""}`}>
                {t.highlight && <div className="overline text-[color:var(--wk-gold)]">Most Chosen</div>}
                <div className="mt-2 font-serif-display text-3xl">{t.name}</div>
                <div className="mt-6 flex items-baseline gap-2">
                  <div className="font-serif-display text-5xl">{t.price}</div>
                  <div className="text-sm text-[color:var(--wk-bone-dim)]">{t.unit}</div>
                </div>
                <ul className="mt-8 space-y-3">
                  {t.perks.map((p) => (
                    <li key={p} className="flex gap-3 items-start text-sm">
                      <Check size={14} className="text-[color:var(--wk-gold)] mt-1 shrink-0" /> {p}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`membership-pick-${t.slug}`}
                  onClick={() => setForm({ ...form, tier: t.slug })}
                  className={`mt-8 w-full ${t.highlight ? "btn-gold" : "btn-ghost"}`}
                >
                  Select {t.name}
                </button>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      <Section testid="membership-form" className="pb-32">
        <Container>
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 card-lux p-10">
              <Eyebrow>Join the founding cohort</Eyebrow>
              <div className="mt-3 font-serif-display text-3xl">You picked: <span className="capitalize text-[color:var(--wk-gold)]">{form.tier}</span></div>
              <form onSubmit={submit} className="mt-8 space-y-6">
                <div>
                  <label className="overline">Name</label>
                  <input required value={form.name} onChange={set("name")} className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--wk-gold)] outline-none py-3 text-lg" data-testid="membership-name" />
                </div>
                <div>
                  <label className="overline">Email</label>
                  <input required type="email" value={form.email} onChange={set("email")} className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--wk-gold)] outline-none py-3 text-lg" data-testid="membership-email" />
                </div>
                <button disabled={busy} className="btn-gold" data-testid="membership-submit">{busy ? "Adding you…" : "Reserve my spot"}</button>
              </form>
            </div>
            <aside className="lg:col-span-5 card-lux p-10 flex flex-col justify-between">
              <div>
                <Eyebrow>Members-only channel</Eyebrow>
                <div className="mt-3 font-serif-display text-3xl leading-tight">Ask the Founder.</div>
                <p className="mt-4 text-[color:var(--wk-bone-dim)] leading-relaxed">
                  A private AMA line to Toriano "The Wolfkeeper" Sanzone. Each month he answers a
                  curated set of member questions on livestream and privately by video.
                </p>
              </div>
              <Link to="/founder-qa" className="btn-ghost mt-8 w-full justify-center" data-testid="membership-qa-link">
                Open the Founder Q&amp;A →
              </Link>
            </aside>
          </div>
        </Container>
      </Section>
    </>
  );
}
